import { defineNuxtPlugin } from "nuxt/app";
import { Surreal } from "surrealdb";

let db: Surreal | undefined;

function getCookie(name: string) {
  const cookieArr = document.cookie.split(";");
  if (!cookieArr) {
    return null;
  }

  for (let i = 0; i < cookieArr.length; i++) {
    const cookiePair = cookieArr[i]?.split("=");
    if (!cookiePair) {
      return null;
    }

    if (name === cookiePair[0]?.trim()) {
      // Decode the cookie value and return
      if (!cookiePair[1]) {
        return null;
      }
      return decodeURIComponent(cookiePair[1]);
    }
  }

  return null;
}

async function initDb(): Promise<Surreal> {
  try {
    if (db === undefined) {
      db = new Surreal();
    }

    const myJWT = getCookie("witness-card");
    if (myJWT === null) {
      window.location.href = "/login/?returnUrl=" +
        encodeURIComponent(window.location.pathname + window.location.search);
      throw new Error("No JWT found in cookies");
    }

    let protocol = "ws://";
    if (window.location.protocol === "https:") {
      protocol = "wss://";
    }
    const host = window.location.hostname;
    const port = window.location.port ? ":" + window.location.port : "";
    const url = protocol + host + port + "/surrealdb/rpc";

    await db.connect(url, {
      // @ts-ignore
      ns: "witness-card",
      dn: "witness-card",
      ac: "user",
    });
    await db.ready;

    await db.authenticate(myJWT);

    return db;
  } catch (err) {
    console.error("Failed to connect to SurrealDB:", err);
    throw err;
  }
}

export default defineNuxtPlugin(() => {
  return {
    provide: {
      surreal: async () => {
        if (db === undefined) {
          return await initDb();
        }

        return db;
      },

      surrealQuery: (sql: string, vars?: any) => {
        const pending = ref<boolean>(true);
        const data = ref([]);
        const error = ref<any>(null);

        const fetchData = async () => {
          try {
            if (!db) {
              db = await initDb();
            }
            const results = await db.query(sql, vars);
            if (!results) {
              return;
            }

            data.value.push(...results[0]);
          } catch (err) {
            error.value = err;
          } finally {
            pending.value = false;
          }
        };

        return {
          pending,
          data,
          error,
          ready: fetchData(), // Queue the fetch operation
        };
      },

      surrealRawQuery: async (sql: string, vars?: any) => {
        if (!db) {
          db = await initDb();
        }
        try {
          const results = await db.query(sql, vars);
          return results;
        } catch (err) {
          throw err;
        }
      },

      surrealLive(table: string, callback: (action: string, result) => any) {
        const pending = ref<boolean>(true);
        const data = ref("");
        const error = ref<any>(null);

        const fetchData = async () => {
          try {
            if (!db) {
              db = await initDb();
            }
            const queryUuid = await db.live(table, callback);
            data.value = queryUuid.toString();
          } catch (err) {
            error.value = err;
          } finally {
            pending.value = false;
          }
        };

        return {
          pending,
          data,
          error,
          ready: fetchData(), // Queue the initial fetch operation
          close: async () => {
            if (!db) {
              db = await initDb();
            }
            if (!data.value) {
              return;
            }

            return db.kill(data.value);
          },
        };
      },
    },
  };
});
